import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Optional,
    Output,
    QueryList,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import type { iccIcon } from '../../icons';
import { TemplateDirective } from '../../template.directive';
import { InputComponent } from '../input/input.component';

@Directive({
    selector: '[iccInplaceFocus]'
})
export class InplaceFocusDirective {
    constructor(private elementRef: ElementRef, @Optional() private host: InputComponent) {}

    focus() {
        setTimeout(() => {
            if (this.host) {
                this.host.focus();
            } else {
                this.elementRef.nativeElement.focus();
            }
        }, 0);
    }
}

@Component({
    selector: 'icc-inplace-display',
    template: '<ng-content></ng-content>',
    host: {
        class: 'icc-element',
    },
})
export class InplaceDisplayComponent {}

@Component({
    selector: 'icc-inplace-content',
    template: '<ng-content></ng-content>',
    host: {
        class: 'icc-element',
    },
})
export class InplaceContentComponent {}

@Component({
    selector: 'icc-inplace',
    templateUrl: './inplace.component.html',
    styleUrls: ['./inplace.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'icc-element',
    },
})
export class InplaceComponent implements AfterContentInit {
    @Input()
    active = false;

    @Input()
    closable = false;

    @Input()
    disabled = false;

    @Input()
    preventClick = false;

    @Input() style: any;

    @Input() styleClass = '';

    @Input() closeIcon: iccIcon = 'close';

    @ContentChildren(TemplateDirective)
    templates!: QueryList<TemplateDirective>;

    @Output() activated: EventEmitter<any> = new EventEmitter();

    @Output() deactivated: EventEmitter<any> = new EventEmitter();

    hover = false;

    displayTemplate!: TemplateRef<any>;

    contentTemplate!: TemplateRef<any>;

    @ContentChildren(InplaceFocusDirective) focusDirective!: QueryList<InplaceFocusDirective>;

    constructor(public cd: ChangeDetectorRef, private elementRef: ElementRef) {}

    ngAfterContentInit() {
        this.templates.forEach((item) => {
            switch (item.getType()) {
                case 'display':
                    this.displayTemplate = item.template;
                    break;

                case 'content':
                    this.contentTemplate = item.template;
                    break;
            }
        });
    }

    onActivateClick(event) {
        if (!this.preventClick) this.activate(event);
    }

    onDeactivateClick(event) {
        if (!this.preventClick) this.deactivate(event);
    }

    activate(event?: Event) {
        if (!this.disabled) {
            this.active = true;
            this.activated.emit(event);
            this.cd.markForCheck();
            setTimeout(() => {
                if (this.focusDirective) {
                    this.focusDirective.forEach((directive) => {
                        directive.focus();
                    });
                }
            }, 0);
        }
    }

    deactivate(event?: Event) {
        if (!this.disabled) {
            this.active = false;
            this.hover = false;
            this.deactivated.emit(event);
            this.cd.markForCheck();
        }
    }

    onKeydown(event: KeyboardEvent) {
        if (event.which === 13) {
            this.activate(event);
            event.preventDefault();
        }
    }

    @HostListener('document:click', ['$event.target'])
    clickOutside(target: HTMLElement) {
        const clickedInside =
            this.elementRef.nativeElement.contains(target) ||
            target.classList.contains('icc-inplace-display') ||
            target.parentElement?.classList.contains('icc-inplace-display');
        if (!clickedInside) {
            this.deactivate();
        }
    }
}
